<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Fire Safety Permit</span>
            </nav>
            <h1 class="page-title">Fire Safety Compliance Certificate</h1>
            <p class="page-subtitle">Apply for fire inspection and safety compliance certification for your establishment</p>
          </div>

          <!-- Progress Steps -->
          <div class="progress-stepper" v-if="currentStep < 5">
            <div
                v-for="(step, index) in steps"
                :key="index"
                class="step-item"
                :class="{
                active: currentStep === index + 1,
                completed: currentStep > index + 1
              }"
            >
              <div class="step-connector" v-if="index > 0"></div>
              <div class="step-circle">
                <svg v-if="currentStep > index + 1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span class="step-label">{{ step }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Panel -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon fire">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path>
                    </svg>
                  </div>
                  <h3>Fire Safety Permit</h3>
                </div>
                <p class="info-description">
                  Fire Safety Compliance Certificates are mandatory for all commercial establishments to ensure fire safety standards are met and protect lives and property.
                </p>

                <div class="info-features">
                  <div class="feature-item">
                    <div class="feature-icon orange">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        <path d="M9 12l2 2 4-4"></path>
                      </svg>
                    </div>
                    <div>
                      <strong>Safety Compliance</strong>
                      <p>Ensure your premises meet standards</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Valid for 1 Year</strong>
                      <p>Annual renewal required</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Digital Certificate</strong>
                      <p>Download after approval</p>
                    </div>
                  </div>
                </div>

                <!-- Who Needs This -->
                <div class="who-needs fire">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path>
                    </svg>
                    Who Needs This Certificate?
                  </h4>
                  <ul>
                    <li>Commercial Buildings & Offices</li>
                    <li>Hotels & Restaurants</li>
                    <li>Factories & Warehouses</li>
                    <li>Schools & Educational Institutions</li>
                    <li>Hospitals & Healthcare Facilities</li>
                    <li>Shopping Malls & Retail Stores</li>
                    <li>Petrol Stations & Fuel Depots</li>
                  </ul>
                </div>

                <!-- Quick Actions Section -->
                <div class="quick-actions-section">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                    Quick Actions
                  </h4>

                  <!-- Check Status Button -->
                  <button class="quick-action-btn" @click="showStatusModal = true">
                    <div class="action-icon blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <div class="action-content">
                      <strong>Check Application Status</strong>
                      <span>Track your application progress</span>
                    </div>
                    <svg class="action-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>

                  <!-- Print License Button -->
                  <router-link to="/print-fire-license" class="quick-action-btn">
                    <div class="action-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                    </div>
                    <div class="action-content">
                      <strong>Print Fire License</strong>
                      <span>Download your certificate</span>
                    </div>
                    <svg class="action-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </router-link>
                </div>

                <!-- Help Contact -->
                <div class="help-contact">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    Need Help?
                  </h4>
                  <div class="contact-info">
                    <div class="contact-item">
                      <span>{{ county?.contact || '0700-000-000' }}</span>
                    </div>
                    <div class="contact-item">
                      <span>{{ county?.email || 'fire@county.go.ke' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Side - Form Panel -->
            <div class="form-panel">
              <!-- Error Alert -->
              <div v-if="error" class="alert alert-error">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                <span>{{ error }}</span>
                <button @click="error = ''" class="alert-close">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <transition name="slide-fade" mode="out-in">
                <!-- Step 1: Find or Register Business -->
                <div v-if="currentStep === 1" key="step1" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">1</span>
                      <span class="step-text">Find Your Establishment</span>
                    </div>
                  </div>

                  <div class="form-card-body">
                    <p class="form-intro">
                      Search for your existing registered business or register a new establishment for fire safety certification.
                    </p>

                    <!-- Search Method Selection -->
                    <div class="method-selection">
                      <label
                          class="method-card"
                          :class="{ selected: searchMethod === 'search' }"
                      >
                        <input type="radio" v-model="searchMethod" value="search" class="hidden-radio" />
                        <div class="method-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                          </svg>
                        </div>
                        <div class="method-content">
                          <strong>Search Existing Business</strong>
                          <p>Find your registered business by Business ID</p>
                        </div>
                      </label>

                      <label
                          class="method-card"
                          :class="{ selected: searchMethod === 'register' }"
                      >
                        <input type="radio" v-model="searchMethod" value="register" class="hidden-radio" />
                        <div class="method-icon green">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                        </div>
                        <div class="method-content">
                          <strong>New Establishment</strong>
                          <p>Register a new establishment for fire permit</p>
                        </div>
                      </label>
                    </div>

                    <!-- Search Form -->
                    <div v-if="searchMethod === 'search'" class="search-section">
                      <div class="form-group">
                        <label class="form-label">Business ID <span class="text-danger">*</span></label>
                        <div class="input-with-button">
                          <input
                              type="text"
                              v-model="businessID"
                              class="form-input"
                              placeholder="Enter Business ID"
                              @keyup.enter="searchBusiness"
                          />
                          <button
                              type="button"
                              class="fetch-btn"
                              @click="searchBusiness"
                              :disabled="!businessID || searchingBusiness"
                          >
                            <span v-if="searchingBusiness" class="spinner small"></span>
                            <span v-else>Search</span>
                          </button>
                        </div>
                      </div>

                      <!-- Business Found Card -->
                      <div v-if="businessFound" class="business-found-card">
                        <div class="business-found-header">
                          <div class="found-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                              <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                          </div>
                          <div>
                            <h4>Business Found</h4>
                            <p>{{ businessDetails.businessID }}</p>
                          </div>
                        </div>
                        <div class="business-details-grid">
                          <div class="detail-row">
                            <span class="detail-label">Business Name</span>
                            <span class="detail-value">{{ businessDetails.businessName }}</span>
                          </div>
                          <div class="detail-row">
                            <span class="detail-label">Location</span>
                            <span class="detail-value">{{ businessDetails.wardName }}, {{ businessDetails.subCountyName }}</span>
                          </div>
                          <div class="detail-row">
                            <span class="detail-label">Physical Address</span>
                            <span class="detail-value">{{ businessDetails.physicalAddress || 'N/A' }}</span>
                          </div>
                          <div class="detail-row">
                            <span class="detail-label">Building/Premises</span>
                            <span class="detail-value">{{ businessDetails.buildingName || 'N/A' }}</span>
                          </div>
                          <div class="detail-row">
                            <span class="detail-label">Owner/Applicant</span>
                            <span class="detail-value">{{ businessDetails.fullNames }}</span>
                          </div>
                          <div class="detail-row">
                            <span class="detail-label">Phone</span>
                            <span class="detail-value">{{ businessDetails.ownerPhone }}</span>
                          </div>
                        </div>
                        <button class="btn btn-primary w-100 mt-3" @click="proceedWithBusiness">
                          Continue with this Business
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12 5 19 12 12 19"></polyline>
                          </svg>
                        </button>
                      </div>

                      <!-- Business Not Found -->
                      <div v-if="businessNotFound" class="not-found-card">
                        <div class="not-found-icon">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                          </svg>
                        </div>
                        <h4>Business Not Found</h4>
                        <p>No business found with this ID. You can register a new establishment instead.</p>
                        <button class="btn btn-secondary" @click="searchMethod = 'register'">
                          Register New Establishment
                        </button>
                      </div>
                    </div>

                    <!-- New Registration Form -->
                    <div v-if="searchMethod === 'register'" class="register-section">
                      <div class="form-group">
                        <label class="form-label">Name of Establishment <span class="text-danger">*</span></label>
                        <input
                            type="text"
                            v-model="businessDetails.businessName"
                            class="form-input"
                            placeholder="e.g., ABC Industries Ltd"
                        />
                      </div>

                      <div class="form-row">
                        <div class="form-group">
                          <label class="form-label">Building/Premises Name</label>
                          <input
                              type="text"
                              v-model="businessDetails.buildingName"
                              class="form-input"
                              placeholder="e.g., Industrial Park"
                          />
                        </div>
                        <div class="form-group">
                          <label class="form-label">Plot Number</label>
                          <input
                              type="text"
                              v-model="businessDetails.plotNumber"
                              class="form-input"
                              placeholder="Enter plot number"
                          />
                        </div>
                      </div>

                      <div class="form-row">
                        <div class="form-group">
                          <label class="form-label">Sub County <span class="text-danger">*</span></label>
                          <div class="select-wrapper">
                            <select v-model="selectedSubCounty" class="form-select" @change="onSubCountyChange">
                              <option value="">-- Select Sub County --</option>
                              <option v-for="sc in subCounties" :key="sc.subCountyID" :value="sc">
                                {{ sc.subCountyName }}
                              </option>
                            </select>
                            <span class="select-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </span>
                          </div>
                        </div>
                        <div class="form-group">
                          <label class="form-label">Ward <span class="text-danger">*</span></label>
                          <div class="select-wrapper">
                            <select v-model="selectedWard" class="form-select" :disabled="!selectedSubCounty">
                              <option value="">-- Select Ward --</option>
                              <option v-for="w in wards" :key="w.wardID" :value="w">
                                {{ w.wardName }}
                              </option>
                            </select>
                            <span class="select-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </span>
                          </div>
                        </div>
                      </div>

                      <div class="form-group">
                        <label class="form-label">Physical Address <span class="text-danger">*</span></label>
                        <textarea
                            v-model="businessDetails.physicalAddress"
                            class="form-textarea"
                            rows="2"
                            placeholder="Enter the physical location of your establishment"
                        ></textarea>
                      </div>

                      <div class="form-row">
                        <div class="form-group">
                          <label class="form-label">Applicant's Full Name <span class="text-danger">*</span></label>
                          <input
                              type="text"
                              v-model="businessDetails.fullNames"
                              class="form-input"
                              placeholder="Enter your full name"
                          />
                        </div>
                        <div class="form-group">
                          <label class="form-label">Postal Address</label>
                          <input
                              type="text"
                              v-model="businessDetails.postalAddress"
                              class="form-input"
                              placeholder="e.g., P.O Box 123-00100"
                          />
                        </div>
                      </div>

                      <div class="form-row">
                        <div class="form-group">
                          <label class="form-label">Phone Number <span class="text-danger">*</span></label>
                          <input
                              type="tel"
                              v-model="businessDetails.ownerPhone"
                              class="form-input"
                              placeholder="07XX XXX XXX"
                          />
                        </div>
                        <div class="form-group">
                          <label class="form-label">Email Address</label>
                          <input
                              type="email"
                              v-model="businessDetails.ownerEmail"
                              class="form-input"
                              placeholder="email@example.com"
                          />
                        </div>
                      </div>

                      <div class="form-group">
                        <label class="form-label">Nature of Business <span class="text-danger">*</span></label>
                        <input
                            type="text"
                            v-model="businessDetails.businessDes"
                            class="form-input"
                            placeholder="e.g., Manufacturing, Warehouse, Hotel"
                        />
                      </div>

                      <div class="form-actions">
                        <button
                            class="btn btn-primary btn-lg"
                            @click="registerAndContinue"
                            :disabled="!canProceedStep1Register || registering"
                        >
                          <span v-if="registering" class="spinner"></span>
                          <span v-else>Continue</span>
                          <svg v-if="!registering" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12 5 19 12 12 19"></polyline>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Step 2: Fire Safety Assessment -->
                <div v-else-if="currentStep === 2" key="step2" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">2</span>
                      <span class="step-text">Fire Safety Assessment</span>
                    </div>
                    <button class="back-btn" @click="prevStep">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      Back
                    </button>
                  </div>

                  <div class="form-card-body">
                    <!-- Business Summary -->
                    <div class="business-summary">
                      <div class="summary-icon fire">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"></path>
                        </svg>
                      </div>
                      <div>
                        <strong>{{ businessDetails.businessName }}</strong>
                        <p>{{ businessDetails.wardName }}, {{ businessDetails.subCountyName }}</p>
                      </div>
                    </div>

                    <!-- Fire Safety Measures -->
                    <div class="safety-measures-section">
                      <div class="section-title-bar">
                        <div class="title-icon orange">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                            <path d="M9 12l2 2 4-4"></path>
                          </svg>
                        </div>
                        <div>
                          <h4>Fire Safety Measures in Place</h4>
                          <p>Select all safety measures currently deployed at your establishment</p>
                        </div>
                      </div>

                      <div class="safety-measures-grid">
                        <label
                            v-for="(measure, index) in fireSafetyMeasures"
                            :key="index"
                            class="safety-measure-item"
                            :class="{ selected: measure.selected }"
                        >
                          <input
                              type="checkbox"
                              v-model="measure.selected"
                              class="hidden-checkbox"
                          />
                          <div class="measure-checkbox">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                          <span class="measure-text">{{ measure.category }}</span>
                        </label>
                      </div>
                    </div>

                    <!-- Recommendations Section -->
                    <div class="recommendations-section">
                      <div class="section-title-bar">
                        <div class="title-icon blue">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                          </svg>
                        </div>
                        <div>
                          <h4>Additional Notes (Optional)</h4>
                          <p>Add any additional information about your fire safety setup</p>
                        </div>
                      </div>

                      <div class="recommendations-list">
                        <div v-for="(rec, index) in recommendations" :key="index" class="recommendation-item">
                          <span class="rec-number">{{ index + 1 }}</span>
                          <input
                              type="text"
                              v-model="rec.text"
                              class="form-input"
                              placeholder="Enter additional note..."
                          />
                          <button type="button" class="remove-rec-btn" @click="removeRecommendation(index)">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                        <button type="button" class="add-rec-btn" @click="addRecommendation">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          Add Note
                        </button>
                      </div>
                    </div>

                    <div class="form-actions">
                      <button class="btn btn-primary btn-lg" @click="nextStep">
                        Continue to Permit Selection
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 3: Select Permit Category & Fees -->
                <div v-else-if="currentStep === 3" key="step3" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">3</span>
                      <span class="step-text">Permit Category & Fees</span>
                    </div>
                    <button class="back-btn" @click="prevStep">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      Back
                    </button>
                  </div>

                  <div class="form-card-body">
                    <!-- Business Summary -->
                    <div class="business-summary">
                      <div class="summary-icon fire">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path>
                        </svg>
                      </div>
                      <div>
                        <strong>{{ businessDetails.businessName }}</strong>
                        <p>{{ businessDetails.wardName }}, {{ businessDetails.subCountyName }}</p>
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="form-label">Permit Category <span class="text-danger">*</span></label>
                      <div class="select-wrapper">
                        <select v-model="selectedCategory" class="form-select" @change="onCategoryChange">
                          <option value="">-- Select Permit Category --</option>
                          <option v-for="cat in incomeTypesMain" :key="cat.incomeTypeId" :value="cat">
                            {{ cat.incomeTypeDescription }}
                          </option>
                        </select>
                        <span class="select-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </span>
                      </div>
                    </div>

                    <div class="form-group" v-if="feesAndChargesMain.length > 0">
                      <label class="form-label">Permit Type <span class="text-danger">*</span></label>
                      <div class="select-wrapper">
                        <select v-model="selectedFee" class="form-select">
                          <option value="">-- Select Permit Type --</option>
                          <option v-for="fee in feesAndChargesMain" :key="fee.feeId" :value="fee">
                            {{ fee.feeDescription }} - KES {{ formatAmount(fee.unitFeeAmount) }}
                          </option>
                        </select>
                        <span class="select-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </span>
                      </div>
                    </div>

                    <!-- Other Applicable Charges -->
                    <div class="other-charges-section">
                      <div class="section-header-bar">
                        <div class="header-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                        </div>
                        <h4>Other Fire Safety Fees (Optional)</h4>
                        <span class="charges-count">{{ incomeTypesOther.length }} categories</span>
                      </div>

                      <div class="add-charge-form">
                        <div class="charge-step">
                          <span class="step-badge blue">Step 1</span>
                          <div class="form-group">
                            <label class="form-label">Select Category</label>
                            <div class="select-wrapper">
                              <select v-model="selectedOtherCategory" class="form-select" @change="onOtherCategoryChange">
                                <option :value="null">-- Select Category --</option>
                                <option v-for="cat in incomeTypesOther" :key="cat.incomeTypeId" :value="cat">
                                  {{ cat.incomeTypeDescription }}
                                </option>
                              </select>
                              <span class="select-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>

                        <div class="charge-step" v-if="selectedOtherCategory && feesAndChargesOther.length > 0">
                          <span class="step-badge green">Step 2</span>
                          <div class="form-group">
                            <label class="form-label">Select Specific Charge</label>
                            <div class="select-wrapper">
                              <select v-model="selectedCharge" class="form-select">
                                <option :value="null">-- Select a charge --</option>
                                <option v-for="charge in feesAndChargesOther" :key="charge.feeId" :value="charge">
                                  {{ charge.feeDescription }} - KES {{ formatAmount(charge.unitFeeAmount) }}
                                </option>
                              </select>
                              <span class="select-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>

                        <div class="charge-step-row" v-if="selectedOtherCategory && feesAndChargesOther.length > 0">
                          <div class="charge-step quantity-step">
                            <span class="step-badge purple">Step 3</span>
                            <div class="form-group">
                              <label class="form-label">Quantity</label>
                              <input
                                  type="number"
                                  v-model.number="chargeQuantity"
                                  class="form-input"
                                  min="1"
                                  placeholder="1"
                              />
                            </div>
                          </div>
                          <button
                              type="button"
                              class="btn btn-add-charge"
                              @click="addChargeToList"
                              :disabled="!selectedCharge"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <line x1="12" y1="5" x2="12" y2="19"></line>
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            Add Charge
                          </button>
                        </div>

                        <div v-if="selectedOtherCategory && feesAndChargesOther.length === 0 && loadingOtherFees" class="loading-charges">
                          <span class="spinner small blue"></span>
                          Loading charges...
                        </div>
                      </div>

                      <!-- Added Charges Table -->
                      <div v-if="addedCharges.length > 0" class="added-charges-table">
                        <table class="charges-table">
                          <thead>
                          <tr>
                            <th>Charge Description</th>
                            <th class="text-center">Qty</th>
                            <th class="text-right">Unit Price</th>
                            <th class="text-right">Total</th>
                            <th class="text-center">Action</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(item, index) in addedCharges" :key="index">
                            <td>{{ item.feeDescription }}</td>
                            <td class="text-center">{{ item.quantity }}</td>
                            <td class="text-right">KES {{ formatAmount(item.unitFeeAmount) }}</td>
                            <td class="text-right">KES {{ formatAmount(item.unitFeeAmount * item.quantity) }}</td>
                            <td class="text-center">
                              <button type="button" class="remove-charge-btn" @click="removeCharge(index)">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                              </button>
                            </td>
                          </tr>
                          </tbody>
                          <tfoot>
                          <tr>
                            <th colspan="3">Total Other Charges</th>
                            <th class="text-right">KES {{ formatAmount(totalAddedCharges) }}</th>
                            <th></th>
                          </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>

                    <!-- Fee Summary -->
                    <div class="fee-summary" v-if="selectedFee">
                      <div class="fee-row">
                        <span>Permit Fee ({{ selectedFee.feeDescription }})</span>
                        <span>KES {{ formatAmount(selectedFee.unitFeeAmount) }}</span>
                      </div>
                      <div class="fee-row" v-if="totalAddedCharges > 0">
                        <span>Other Charges ({{ addedCharges.length }} items)</span>
                        <span>KES {{ formatAmount(totalAddedCharges) }}</span>
                      </div>
                      <div class="fee-row total">
                        <span>Total Amount</span>
                        <span>KES {{ formatAmount(totalAmount) }}</span>
                      </div>
                    </div>

                    <div class="form-actions">
                      <button
                          class="btn btn-primary btn-lg"
                          @click="nextStep"
                          :disabled="!canProceedStep3"
                      >
                        Continue to Review
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 4: Review & Submit -->
                <div v-else-if="currentStep === 4" key="step4" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">4</span>
                      <span class="step-text">Review & Submit</span>
                    </div>
                    <button class="back-btn" @click="prevStep">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      Back
                    </button>
                  </div>

                  <div class="form-card-body">
                    <p class="form-intro">
                      Review your fire permit application details before submitting.
                    </p>

                    <!-- Summary Sections -->
                    <div class="summary-section">
                      <div class="summary-header">
                        <h4>Establishment Details</h4>
                        <button class="edit-btn" @click="goToStep(1)">Edit</button>
                      </div>
                      <div class="summary-grid">
                        <div class="summary-item">
                          <span class="summary-label">Business Name</span>
                          <span class="summary-value">{{ businessDetails.businessName }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Business ID</span>
                          <span class="summary-value">{{ businessDetails.businessID || 'New Registration' }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Location</span>
                          <span class="summary-value">{{ businessDetails.wardName }}, {{ businessDetails.subCountyName }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Physical Address</span>
                          <span class="summary-value">{{ businessDetails.physicalAddress || 'N/A' }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Applicant</span>
                          <span class="summary-value">{{ businessDetails.fullNames }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Phone</span>
                          <span class="summary-value">{{ businessDetails.ownerPhone }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="summary-section">
                      <div class="summary-header">
                        <h4>Fire Safety Measures</h4>
                        <button class="edit-btn" @click="goToStep(2)">Edit</button>
                      </div>
                      <div class="safety-summary">
                        <div v-if="selectedSafetyMeasures.length > 0" class="measures-list">
                          <span v-for="(measure, index) in selectedSafetyMeasures" :key="index" class="measure-tag">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            {{ measure.category }}
                          </span>
                        </div>
                        <p v-else class="no-measures">No safety measures selected</p>
                      </div>
                    </div>

                    <div class="summary-section">
                      <div class="summary-header">
                        <h4>Permit Details</h4>
                        <button class="edit-btn" @click="goToStep(3)">Edit</button>
                      </div>
                      <div class="summary-grid">
                        <div class="summary-item">
                          <span class="summary-label">Category</span>
                          <span class="summary-value">{{ selectedCategory?.incomeTypeDescription }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Permit Type</span>
                          <span class="summary-value">{{ selectedFee?.feeDescription }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Invoice Preview -->
                    <div class="invoice-preview">
                      <h4>Invoice Preview</h4>
                      <table class="invoice-table">
                        <thead>
                        <tr>
                          <th>Description</th>
                          <th class="text-center">Qty</th>
                          <th class="text-right">Unit Price</th>
                          <th class="text-right">Amount</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                          <td>{{ selectedFee?.feeDescription }}</td>
                          <td class="text-center">1</td>
                          <td class="text-right">KES {{ formatAmount(selectedFee?.unitFeeAmount) }}</td>
                          <td class="text-right">KES {{ formatAmount(selectedFee?.unitFeeAmount) }}</td>
                        </tr>
                        <tr v-for="item in addedCharges" :key="item.feeId">
                          <td>{{ item.feeDescription }}</td>
                          <td class="text-center">{{ item.quantity }}</td>
                          <td class="text-right">KES {{ formatAmount(item.unitFeeAmount) }}</td>
                          <td class="text-right">KES {{ formatAmount(item.unitFeeAmount * item.quantity) }}</td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr>
                          <th colspan="3">Total Payable</th>
                          <th class="text-right">KES {{ formatAmount(totalAmount) }}</th>
                        </tr>
                        </tfoot>
                      </table>
                    </div>

                    <!-- Payment Notice -->
                    <div class="payment-notice fire">
                      <div class="notice-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                      </div>
                      <div>
                        <strong>Payment Information</strong>
                        <p>After submission, you will receive an invoice via SMS to <strong>{{ businessDetails.ownerPhone }}</strong>. Pay using M-Pesa and await inspection by fire officers.</p>
                      </div>
                    </div>

                    <!-- Declaration -->
                    <div class="declaration-box">
                      <label class="declaration-label">
                        <input type="checkbox" v-model="declarationAccepted" />
                        <span class="checkbox-mark">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </span>
                        <span class="declaration-text">
                          I declare that the fire safety information provided is accurate. I understand that a fire officer will inspect my premises and the certificate will be issued after payment and successful inspection.
                        </span>
                      </label>
                    </div>

                    <div class="form-actions">
                      <button
                          class="btn btn-success btn-lg"
                          @click="submitApplication"
                          :disabled="!declarationAccepted || submitting"
                      >
                        <span v-if="submitting" class="spinner white"></span>
                        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        {{ submitting ? 'Submitting...' : 'Submit Application' }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 5: Success -->
                <div v-else-if="currentStep === 5" key="step5" class="form-card success-card">
                  <div class="form-card-body">
                    <div class="success-content">
                      <div class="success-icon fire">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                      </div>
                      <h2>Application Submitted!</h2>
                      <p class="success-message">Your fire safety permit application has been submitted successfully.</p>

                      <div class="invoice-card fire">
                        <span class="invoice-label">Invoice Number</span>
                        <span class="invoice-number">{{ billDetails.billNo }}</span>
                        <div class="invoice-amount">
                          <span>Total Amount</span>
                          <strong>KES {{ formatAmount(billDetails.detailAmount) }}</strong>
                        </div>
                      </div>

                      <div class="notification-card">
                        <div class="notification-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                          </svg>
                        </div>
                        <div class="notification-text">
                          <strong>Payment Instructions Sent</strong>
                          <p>Invoice details have been sent to <strong>{{ businessDetails.ownerPhone }}</strong></p>
                        </div>
                      </div>

                      <div class="next-steps">
                        <h4>What's Next?</h4>
                        <ul>
                          <li>
                            <span class="step-num">1</span>
                            <div>
                              <strong>Make Payment</strong>
                              <p>Pay using M-Pesa Paybill or visit our office</p>
                            </div>
                          </li>
                          <li>
                            <span class="step-num">2</span>
                            <div>
                              <strong>Fire Inspection</strong>
                              <p>Fire officer will inspect your establishment</p>
                            </div>
                          </li>
                          <li>
                            <span class="step-num">3</span>
                            <div>
                              <strong>Approval & Certificate</strong>
                              <p>Certificate issued after successful inspection</p>
                            </div>
                          </li>
                        </ul>
                      </div>

                      <div class="success-actions">
                        <button class="btn btn-primary" @click="printBill" v-if="billDetails">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 6 2 18 2 18 9"></polyline>
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                            <rect x="6" y="14" width="12" height="8"></rect>
                          </svg>
                          Print Invoice
                        </button>
                        <button class="btn btn-secondary" @click="resetForm">
                          New Application
                        </button>
                        <router-link to="/" class="btn btn-outline">
                          Back to Home
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Check Status Modal -->
    <transition name="modal-fade">
      <div v-if="showStatusModal" class="modal-overlay" @click.self="closeStatusModal">
        <div class="status-modal">
          <div class="modal-header">
            <div class="modal-title-wrapper">
              <div class="modal-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h3>Check Application Status</h3>
            </div>
            <button class="modal-close" @click="closeStatusModal">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <!-- Search Form -->
            <div v-if="!statusResult" class="status-search-form">
              <p class="modal-description">Enter your Business ID or Invoice Number to check your application status.</p>

              <div class="search-tabs">
                <button
                    type="button"
                    class="search-tab"
                    :class="{ active: statusSearchType === 'businessID' }"
                    @click="statusSearchType = 'businessID'"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"></path>
                  </svg>
                  Business ID
                </button>
                <button
                    type="button"
                    class="search-tab"
                    :class="{ active: statusSearchType === 'invoiceNo' }"
                    @click="statusSearchType = 'invoiceNo'"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                  Invoice Number
                </button>
              </div>

              <div class="form-group">
                <label class="form-label">{{ statusSearchType === 'businessID' ? 'Business ID' : 'Invoice Number' }}</label>
                <input
                    type="text"
                    v-model="statusSearchValue"
                    class="form-input"
                    :placeholder="statusSearchType === 'businessID' ? 'Enter Business ID' : 'Enter Invoice Number (e.g., BLL0121015619700)'"
                    @keyup.enter="searchStatus"
                />
              </div>

              <div v-if="statusError" class="status-error">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                {{ statusError }}
              </div>

              <button class="btn btn-primary btn-block" @click="searchStatus" :disabled="!statusSearchValue || statusLoading">
                <span v-if="statusLoading" class="spinner small white"></span>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                {{ statusLoading ? 'Searching...' : 'Check Status' }}
              </button>
            </div>

            <!-- Status Result -->
            <div v-else class="status-result">
              <!-- Status Header -->
              <div class="status-result-header" :class="getStatusClass(statusResult.licenseStatus)">
                <div class="status-icon-wrapper">
                  <svg v-if="statusResult.licenseStatus === 'Active'" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  <svg v-else-if="statusResult.licenseStatus === 'Expired'" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <svg v-else width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <div class="status-info">
                  <span class="status-badge" :class="getStatusClass(statusResult.licenseStatus)">{{ statusResult.licenseStatus }}</span>
                  <p class="status-message">{{ statusResult.message }}</p>
                </div>
              </div>

              <!-- Business Info -->
              <div class="status-details">
                <div class="detail-row">
                  <span class="detail-label">Business ID</span>
                  <span class="detail-value">{{ statusResult.businessID }}</span>
                </div>
                <div class="detail-row" v-if="statusResult.businessName">
                  <span class="detail-label">Business Name</span>
                  <span class="detail-value">{{ statusResult.businessName }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Invoice Number</span>
                  <span class="detail-value">{{ statusResult.billNo }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Valid From</span>
                  <span class="detail-value">{{ formatDisplayDate(statusResult.startDate) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Valid Until</span>
                  <span class="detail-value">{{ formatDisplayDate(statusResult.endDate) }}</span>
                </div>
              </div>

              <!-- Timeline -->
              <div class="status-timeline" v-if="statusTimeline.length > 0">
                <h4>Application Progress</h4>
                <div class="timeline-steps">
                  <div
                      v-for="(step, index) in statusTimeline"
                      :key="index"
                      class="timeline-step"
                      :class="{ completed: step.completed === 'true', current: isCurrentStep(index) }"
                  >
                    <div class="step-marker">
                      <svg v-if="step.completed === 'true'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span v-else>{{ index + 1 }}</span>
                    </div>
                    <div class="step-content">
                      <strong>{{ step.status }}</strong>
                      <span v-if="step.updatedDate && step.updatedDate !== '-'" class="step-date">{{ formatDisplayDate(step.updatedDate) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div class="status-actions">
                <button v-if="statusResult.printable" class="btn btn-primary" @click="printFromStatus">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                    <rect x="6" y="14" width="12" height="8"></rect>
                  </svg>
                  Print Certificate
                </button>
                <button class="btn btn-secondary" @click="resetStatusSearch">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  Search Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <FooterComponent />
  </div>
</template>

<script>
import { biller, fire, trade, execute } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "FireLicensing",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      county: null,
      currentStep: 1,
      steps: ['Business', 'Safety', 'Permit', 'Review'],
      error: '',

      // Search/Registration
      searchMethod: '',
      businessID: '',
      searchingBusiness: false,
      businessFound: false,
      businessNotFound: false,
      registering: false,

      // Business details
      businessDetails: {
        id: null,
        businessID: null,
        businessName: '',
        subCountyID: null,
        subCountyName: '',
        wardID: null,
        wardName: '',
        plotNumber: '',
        physicalAddress: '',
        buildingName: '',
        postalAddress: '',
        postalCode: '',
        fullNames: '',
        ownerPhone: '',
        ownerEmail: '',
        businessDes: ''
      },

      // Location
      subCounties: [],
      wards: [],
      selectedSubCounty: null,
      selectedWard: null,

      // Fire Safety Measures
      fireSafetyMeasures: [],
      recommendations: [],

      // Categories & Fees
      incomeTypesMain: [],
      incomeTypesOther: [],
      feesAndChargesMain: [],
      feesAndChargesOther: [],
      selectedCategory: null,
      selectedFee: null,
      selectedOtherCategory: null,
      selectedCharge: null,
      chargeQuantity: 1,
      addedCharges: [],
      loadingOtherFees: false,

      // Submit
      declarationAccepted: false,
      submitting: false,
      billDetails: null,
      billInfo: [],

      // Check Status Modal
      showStatusModal: false,
      statusSearchType: 'businessID',
      statusSearchValue: '',
      statusLoading: false,
      statusError: '',
      statusResult: null,
      statusTimeline: []
    };
  },

  computed: {
    canProceedStep1Register() {
      return this.businessDetails.businessName &&
          this.selectedSubCounty &&
          this.selectedWard &&
          this.businessDetails.physicalAddress &&
          this.businessDetails.fullNames &&
          this.businessDetails.ownerPhone &&
          this.businessDetails.businessDes;
    },

    canProceedStep3() {
      return this.selectedCategory && this.selectedFee;
    },

    selectedSafetyMeasures() {
      return this.fireSafetyMeasures.filter(m => m.selected);
    },

    safetyMeasuresString() {
      return this.selectedSafetyMeasures.map(m => m.category).join(', ');
    },

    recommendationsString() {
      return this.recommendations.filter(r => r.text.trim()).map(r => r.text).join(', ');
    },

    totalAddedCharges() {
      return this.addedCharges.reduce((sum, item) => sum + (parseFloat(item.unitFeeAmount || 0) * (item.quantity || 1)), 0);
    },

    totalAmount() {
      const baseFee = this.selectedFee ? parseFloat(this.selectedFee.unitFeeAmount) : 0;
      return baseFee + this.totalAddedCharges;
    }
  },

  watch: {
    selectedSubCounty(newVal) {
      if (newVal) {
        this.businessDetails.subCountyID = newVal.subCountyID;
        this.businessDetails.subCountyName = newVal.subCountyName;
        this.getWards(newVal.subCountyID);
      }
    },
    selectedWard(newVal) {
      if (newVal) {
        this.businessDetails.wardID = newVal.wardID;
        this.businessDetails.wardName = newVal.wardName;
      }
    }
  },

  mounted() {
    this.getCounty();
    this.getSubCounties();
    this.getFireMeasures();
    this.getIncomeTypesMain();
    this.getIncomeTypesOther();
  },

  methods: {
    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, biller);
        if (res.data.success) {
          this.county = res.data.data.county;
        }
      } catch (e) { console.error(e); }
    },

    async getSubCounties() {
      try {
        const data = new FormData();
        data.append("function", "getSubCounty");
        const res = await execute(data, biller);
        if (res.data.success) {
          this.subCounties = res.data.data.subCounties;
        }
      } catch (e) { console.error(e); }
    },

    async getWards(subCountyID) {
      try {
        this.wards = [];
        this.selectedWard = null;
        const data = new FormData();
        data.append("function", "getWards");
        data.append("subCountyID", subCountyID);
        const res = await execute(data, biller);
        if (res.data.success) {
          this.wards = res.data.data.wards;
        }
      } catch (e) { console.error(e); }
    },

    onSubCountyChange() {
      this.selectedWard = null;
      this.wards = [];
    },

    async getFireMeasures() {
      try {
        const data = new FormData();
        data.append("function", "getFireMeasures");
        const res = await execute(data, fire);
        if (res.data.success) {
          this.fireSafetyMeasures = res.data.data.fireSafety.map(item => ({
            ...item,
            selected: false
          }));
        }
      } catch (e) { console.error(e); }
    },

    async getIncomeTypesMain() {
      try {
        const data = new FormData();
        data.append("function", "getIncomeTypes");
        data.append("incomeTypePrefix", "FIREINSPECTION");
        data.append("keyword", "Certificate");
        const res = await execute(data, biller);
        if (res.data.success) {
          this.incomeTypesMain = res.data.data.incomeTypes;
        }
      } catch (e) { console.error(e); }
    },

    async getIncomeTypesOther() {
      try {
        const data = new FormData();
        data.append("function", "getIncomeTypes");
        data.append("incomeTypePrefix", "FIREINSPECTION");
        data.append("keyword", "Other");
        const res = await execute(data, biller);
        if (res.data.success) {
          this.incomeTypesOther = res.data.data.incomeTypes;
        }
      } catch (e) { console.error(e); }
    },

    async onCategoryChange() {
      if (!this.selectedCategory) return;
      this.selectedFee = null;
      this.feesAndChargesMain = [];
      try {
        const data = new FormData();
        data.append("function", "getFeesAndCharges");
        data.append("incomeTypeId", this.selectedCategory.incomeTypeId);
        const res = await execute(data, biller);
        if (res.data.success) {
          this.feesAndChargesMain = res.data.data.feesAndCharges;
        }
      } catch (e) { console.error(e); }
    },

    async onOtherCategoryChange() {
      if (!this.selectedOtherCategory) {
        this.feesAndChargesOther = [];
        return;
      }
      this.selectedCharge = null;
      this.feesAndChargesOther = [];
      this.loadingOtherFees = true;
      try {
        const data = new FormData();
        data.append("function", "getFeesAndCharges");
        data.append("incomeTypeId", this.selectedOtherCategory.incomeTypeId);
        const res = await execute(data, biller);
        if (res.data.success) {
          this.feesAndChargesOther = res.data.data.feesAndCharges;
        }
      } catch (e) { console.error(e); }
      finally {
        this.loadingOtherFees = false;
      }
    },

    addChargeToList() {
      if (!this.selectedCharge) return;
      const existingIndex = this.addedCharges.findIndex(c => c.feeId === this.selectedCharge.feeId);
      if (existingIndex >= 0) {
        this.addedCharges[existingIndex].quantity += (this.chargeQuantity || 1);
      } else {
        this.addedCharges.push({
          ...this.selectedCharge,
          quantity: this.chargeQuantity || 1
        });
      }
      this.selectedCharge = null;
      this.chargeQuantity = 1;
    },

    removeCharge(index) {
      this.addedCharges.splice(index, 1);
    },

    addRecommendation() {
      this.recommendations.push({ text: '' });
    },

    removeRecommendation(index) {
      this.recommendations.splice(index, 1);
    },

    async searchBusiness() {
      if (!this.businessID.trim()) return;
      this.searchingBusiness = true;
      this.businessFound = false;
      this.businessNotFound = false;
      this.error = '';

      try {
        const data = new FormData();
        data.append("function", "getBusiness");
        data.append("businessID", this.businessID.trim());
        const res = await execute(data, trade);

        if (res.data.success && res.data.data.business) {
          this.businessDetails = res.data.data.business;
          this.businessFound = true;
        } else {
          this.businessNotFound = true;
        }
      } catch (e) {
        this.error = 'Error searching for business. Please try again.';
      } finally {
        this.searchingBusiness = false;
      }
    },

    proceedWithBusiness() {
      this.registerBusinessForFire();
    },

    async registerBusinessForFire() {
      this.registering = true;
      this.error = '';
      try {
        const data = new FormData();
        data.append("function", "registerBusiness");
        data.append("business", JSON.stringify(this.businessDetails));
        const res = await execute(data, fire);
        if (res.data.success) {
          this.businessDetails = res.data.data.business;
          this.currentStep = 2;
        } else {
          this.error = res.data.message || 'Failed to register business for fire permit.';
        }
      } catch (e) {
        this.error = 'Error registering business. Please try again.';
      } finally {
        this.registering = false;
      }
    },

    async registerAndContinue() {
      this.registering = true;
      this.error = '';
      try {
        const data = new FormData();
        data.append("function", "registerBusiness");
        data.append("business", JSON.stringify(this.businessDetails));
        const res = await execute(data, fire);
        if (res.data.success) {
          this.businessDetails = res.data.data.business;
          this.currentStep = 2;
        } else {
          this.error = res.data.message || 'Failed to register business.';
        }
      } catch (e) {
        this.error = 'Error registering business. Please try again.';
      } finally {
        this.registering = false;
      }
    },

    nextStep() {
      if (this.currentStep < 5) {
        this.currentStep++;
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    goToStep(step) {
      this.currentStep = step;
    },

    formatAmount(amount) {
      if (!amount) return '0';
      return new Intl.NumberFormat('en-KE').format(parseFloat(amount));
    },

    async submitApplication() {
      this.submitting = true;
      this.error = '';

      try {
        const billItems = [];
        const sessionNames = sessionStorage.getItem("sessionNames") || this.businessDetails.fullNames;
        const sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';
        const sessionPhone = sessionStorage.getItem("sessionPhoneNumber") || this.businessDetails.ownerPhone;

        // Build description with safety measures and recommendations
        const description = `[${this.safetyMeasuresString}${this.recommendationsString ? ', Recommendations: ' + this.recommendationsString : ''}]`;

        // Add main permit fee
        if (this.selectedFee) {
          billItems.push({
            feeId: this.selectedFee.feeId,
            feeDescription: this.selectedFee.feeDescription,
            unitFeeAmount: this.selectedFee.unitFeeAmount,
            accountNo: this.selectedFee.accountNo,
            costCenterNo: this.selectedFee.costCenterNo,
            incomeTypeId: this.selectedFee.incomeTypeId,
            amount: this.selectedFee.unitFeeAmount,
            quantity: 1,
            customer: `Business ID: ${this.businessDetails.businessID}`,
            revenueStreamItem: this.businessDetails.businessName,
            subCountyID: this.businessDetails.subCountyID,
            subCountyName: this.businessDetails.subCountyName,
            wardID: this.businessDetails.wardID,
            wardName: this.businessDetails.wardName,
            idNo: sessionIdNo,
            phoneNumber: sessionPhone,
            names: sessionNames,
            customerPhoneNumber: this.businessDetails.ownerPhone,
            description: description
          });
        }

        // Add other charges
        this.addedCharges.forEach(item => {
          billItems.push({
            feeId: item.feeId,
            feeDescription: item.feeDescription,
            unitFeeAmount: item.unitFeeAmount * item.quantity,
            accountNo: item.accountNo,
            costCenterNo: item.costCenterNo,
            incomeTypeId: item.incomeTypeId,
            amount: item.unitFeeAmount * item.quantity,
            quantity: item.quantity,
            customer: `Business ID: ${this.businessDetails.businessID}`,
            revenueStreamItem: this.businessDetails.businessName,
            subCountyID: this.businessDetails.subCountyID,
            subCountyName: this.businessDetails.subCountyName,
            wardID: this.businessDetails.wardID,
            wardName: this.businessDetails.wardName,
            idNo: sessionIdNo,
            phoneNumber: sessionPhone,
            names: sessionNames,
            customerPhoneNumber: this.businessDetails.ownerPhone,
            description: ''
          });
        });

        // Generate bill
        const data = new FormData();
        data.append("function", "generateBill3");
        data.append("billItem", JSON.stringify(billItems));

        const res = await execute(data, biller);

        if (res.data.success) {
          this.billDetails = res.data.data.bill.billDetails;
          this.billInfo = res.data.data.bill.billInfo;
          this.currentStep = 5;
        } else {
          this.error = res.data.message || 'Failed to submit application.';
        }
      } catch (e) {
        this.error = 'Error submitting application. Please try again.';
        console.error(e);
      } finally {
        this.submitting = false;
      }
    },

    printBill() {
      if (!this.billDetails?.billNo) return;
      localStorage['params'] = JSON.stringify({ billNo: this.billDetails.billNo });
      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    },

    resetForm() {
      this.currentStep = 1;
      this.searchMethod = '';
      this.businessID = '';
      this.businessFound = false;
      this.businessNotFound = false;
      this.businessDetails = {
        id: null,
        businessID: null,
        businessName: '',
        subCountyID: null,
        subCountyName: '',
        wardID: null,
        wardName: '',
        plotNumber: '',
        physicalAddress: '',
        buildingName: '',
        postalAddress: '',
        postalCode: '',
        fullNames: '',
        ownerPhone: '',
        ownerEmail: '',
        businessDes: ''
      };
      this.selectedSubCounty = null;
      this.selectedWard = null;
      this.fireSafetyMeasures.forEach(m => m.selected = false);
      this.recommendations = [];
      this.selectedCategory = null;
      this.selectedFee = null;
      this.selectedOtherCategory = null;
      this.selectedCharge = null;
      this.chargeQuantity = 1;
      this.addedCharges = [];
      this.feesAndChargesMain = [];
      this.feesAndChargesOther = [];
      this.declarationAccepted = false;
      this.billDetails = null;
      this.error = '';
    },

    // Status Modal Methods
    closeStatusModal() {
      this.showStatusModal = false;
      this.resetStatusSearch();
    },

    resetStatusSearch() {
      this.statusSearchValue = '';
      this.statusResult = null;
      this.statusTimeline = [];
      this.statusError = '';
    },

    async searchStatus() {
      if (!this.statusSearchValue.trim()) {
        this.statusError = `Please enter a ${this.statusSearchType === 'businessID' ? 'Business ID' : 'Invoice Number'}`;
        return;
      }

      this.statusLoading = true;
      this.statusError = '';
      this.statusResult = null;
      this.statusTimeline = [];

      try {
        // Check status
        const statusData = new FormData();
        statusData.append("function", "checkFireLicenseStatus");
        if (this.statusSearchType === 'businessID') {
          statusData.append("businessID", this.statusSearchValue.trim());
        } else {
          statusData.append("billNo", this.statusSearchValue.trim());
        }

        const statusRes = await execute(statusData, fire);

        if (statusRes.data.success && statusRes.data.data.hasLicense) {
          this.statusResult = statusRes.data.data;

          // Get timeline
          const timelineData = new FormData();
          timelineData.append("function", "getPermitStatusTimeline");
          if (this.statusSearchType === 'businessID') {
            timelineData.append("businessID", this.statusSearchValue.trim());
          } else {
            timelineData.append("billNo", this.statusSearchValue.trim());
          }

          const timelineRes = await execute(timelineData, fire);
          if (timelineRes.data.success && timelineRes.data.data.timeline) {
            // Get unique steps (latest for each statusID)
            const timelineMap = new Map();
            timelineRes.data.data.timeline.forEach(item => {
              const key = item.statusID;
              if (!timelineMap.has(key)) {
                timelineMap.set(key, item);
              }
            });
            this.statusTimeline = Array.from(timelineMap.values()).sort((a, b) => parseInt(a.statusID) - parseInt(b.statusID));
          }
        } else {
          this.statusError = 'No fire license application found with the provided information.';
        }
      } catch (e) {
        console.error(e);
        this.statusError = 'Error checking status. Please try again.';
      } finally {
        this.statusLoading = false;
      }
    },

    getStatusClass(status) {
      switch (status) {
        case 'Active': return 'success';
        case 'Expired': return 'expired';
        case 'Pending Approval': return 'pending';
        case 'Awaiting Payment': return 'warning';
        default: return 'pending';
      }
    },

    isCurrentStep(index) {
      // Find the last completed step
      for (let i = this.statusTimeline.length - 1; i >= 0; i--) {
        if (this.statusTimeline[i].completed === 'true') {
          return i === index;
        }
      }
      return index === 0;
    },

    formatDisplayDate(dateStr) {
      if (!dateStr || dateStr === '-') return 'N/A';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      } catch (e) {
        return dateStr;
      }
    },

    printFromStatus() {
      if (!this.statusResult?.billNo) return;
      localStorage['params'] = JSON.stringify({
        billNo: this.statusResult.billNo,
        businessID: this.statusResult.businessID
      });
      const routeData = this.$router.resolve({ name: 'fire-certificate' });
      window.open(routeData.href, '_blank');
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper { min-height: 100vh; display: flex; flex-direction: column; background: #f8fafc; }
.main-content { flex: 1; padding-top: 72px; }

/* Hero */
.page-hero { background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%); padding: 48px 0 60px; color: white; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
.breadcrumb-nav { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; font-size: 14px; }
.breadcrumb-link { color: rgba(255,255,255,0.7); text-decoration: none; }
.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }
.page-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 32px; font-weight: 800; margin-bottom: 8px; }
.page-subtitle { opacity: 0.9; font-size: 16px; margin-bottom: 32px; }

/* Progress Stepper */
.progress-stepper { display: flex; justify-content: center; background: rgba(255,255,255,0.1); backdrop-filter: blur(10px); border-radius: 16px; padding: 24px 32px; }
.step-item { display: flex; align-items: center; gap: 12px; }
.step-connector { width: 60px; height: 3px; background: rgba(255,255,255,0.3); margin: 0 12px; border-radius: 2px; }
.step-item.completed .step-connector { background: #fbbf24; }
.step-circle { width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 15px; }
.step-item.active .step-circle { background: white; color: #dc2626; transform: scale(1.1); box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
.step-item.completed .step-circle { background: #fbbf24; color: #78350f; }
.step-label { font-size: 14px; font-weight: 500; opacity: 0.8; }
.step-item.active .step-label, .step-item.completed .step-label { opacity: 1; font-weight: 600; }

/* Content */
.content-section { padding: 48px 0 80px; }
.content-grid { display: grid; grid-template-columns: 1fr 1.5fr; gap: 32px; align-items: start; }

/* Info Panel */
.info-panel { position: sticky; top: 96px; }
.info-card { background: white; border-radius: 16px; padding: 32px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
.info-card-header { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
.info-icon { width: 56px; height: 56px; background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%); border-radius: 14px; display: flex; align-items: center; justify-content: center; color: #0f4c81; }
.info-icon.fire { background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%); color: #dc2626; }
.info-card-header h3 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 700; color: #1e293b; }
.info-description { color: #64748b; line-height: 1.6; margin-bottom: 24px; }
.info-features { display: flex; flex-direction: column; gap: 16px; margin-bottom: 24px; }
.feature-item { display: flex; gap: 12px; padding: 16px; background: #f8fafc; border-radius: 12px; }
.feature-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.feature-icon.orange { background: #ffedd5; color: #ea580c; }
.feature-icon.green { background: #d1fae5; color: #059669; }
.feature-icon.blue { background: #dbeafe; color: #2563eb; }
.feature-item strong { display: block; font-size: 14px; color: #1e293b; margin-bottom: 2px; }
.feature-item p { font-size: 13px; color: #64748b; margin: 0; }

.who-needs { padding: 20px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 12px; margin-bottom: 20px; }
.who-needs.fire { background: #fee2e2; border-color: #fca5a5; }
.who-needs h4 { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #92400e; margin-bottom: 12px; }
.who-needs.fire h4 { color: #991b1b; }
.who-needs ul { list-style: none; padding: 0; margin: 0; }
.who-needs li { font-size: 13px; color: #78350f; padding: 4px 0; padding-left: 16px; position: relative; }
.who-needs.fire li { color: #7f1d1d; }
.who-needs li::before { content: '•'; position: absolute; left: 0; color: #d97706; }
.who-needs.fire li::before { color: #dc2626; }

/* Print Fire License Section */
.print-license-section { padding: 20px; background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); border: 1px solid #a7f3d0; border-radius: 12px; margin-bottom: 20px; }
.print-license-section h4 { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #065f46; margin-bottom: 8px; }
.print-license-section p { font-size: 13px; color: #047857; margin: 0 0 16px 0; }
.btn-print-license { display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; padding: 12px 20px; background: #059669; color: white; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; text-decoration: none; cursor: pointer; transition: all 0.2s; }
.btn-print-license:hover { background: #047857; transform: translateY(-1px); }

.help-contact { padding: 20px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; }
.help-contact h4 { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #475569; margin-bottom: 12px; }
.contact-info { display: flex; flex-direction: column; gap: 6px; }
.contact-item { font-size: 14px; color: #64748b; }

/* Form Card */
.form-card { background: white; border-radius: 16px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); overflow: hidden; }
.form-card-header { padding: 24px 32px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between; }
.step-badge { display: flex; align-items: center; gap: 10px; }
.step-badge .step-number { width: 28px; height: 28px; background: #dc2626; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 13px; }
.step-badge .step-text { font-weight: 600; color: #1e293b; }
.back-btn { display: flex; align-items: center; gap: 8px; padding: 8px 16px; background: none; border: 1px solid #e2e8f0; border-radius: 8px; color: #64748b; font-size: 14px; cursor: pointer; }
.back-btn:hover { background: #f1f5f9; color: #1e293b; }
.form-card-body { padding: 32px; }
.form-intro { color: #64748b; margin-bottom: 24px; line-height: 1.6; }

/* Alert */
.alert { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-radius: 10px; font-size: 14px; margin-bottom: 20px; }
.alert-error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }
.alert-close { margin-left: auto; background: none; border: none; cursor: pointer; padding: 4px; color: inherit; }

/* Method Selection */
.method-selection { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
.method-card { padding: 24px; background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 12px; cursor: pointer; transition: all 0.2s; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 12px; }
.method-card:hover { border-color: #dc2626; }
.method-card.selected { background: #fee2e2; border-color: #dc2626; }
.hidden-radio, .hidden-checkbox { display: none; }
.method-icon { width: 64px; height: 64px; background: #fee2e2; border-radius: 16px; display: flex; align-items: center; justify-content: center; color: #dc2626; }
.method-icon.green { background: #d1fae5; color: #059669; }
.method-content strong { display: block; font-size: 15px; color: #1e293b; margin-bottom: 4px; }
.method-content p { font-size: 13px; color: #64748b; margin: 0; }

/* Form Elements */
.form-group { margin-bottom: 20px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.form-label { display: block; font-size: 14px; font-weight: 600; color: #1e293b; margin-bottom: 8px; }
.text-danger { color: #dc2626; }
.form-input, .form-textarea { width: 100%; padding: 14px 16px; border: 2px solid #e2e8f0; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 15px; transition: all 0.2s; background: white; }
.form-input:focus, .form-textarea:focus { outline: none; border-color: #dc2626; box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.1); }
.select-wrapper { position: relative; }
.form-select { width: 100%; padding: 14px 44px 14px 16px; border: 2px solid #e2e8f0; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 15px; background: white; cursor: pointer; appearance: none; }
.form-select:focus { outline: none; border-color: #dc2626; box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.1); }
.form-select:disabled { background: #f8fafc; cursor: not-allowed; }
.select-icon { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }

.input-with-button { display: flex; gap: 8px; }
.input-with-button .form-input { flex: 1; }
.fetch-btn { padding: 14px 24px; background: #dc2626; color: white; border: none; border-radius: 10px; font-weight: 600; cursor: pointer; transition: background 0.2s; }
.fetch-btn:hover:not(:disabled) { background: #b91c1c; }
.fetch-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* Business Found Card */
.business-found-card { background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); border: 1px solid #a7f3d0; border-radius: 12px; padding: 20px; margin-top: 20px; }
.business-found-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid #a7f3d0; }
.found-icon { width: 44px; height: 44px; background: #10b981; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; }
.business-found-header h4 { font-size: 16px; font-weight: 700; color: #065f46; margin: 0; }
.business-found-header p { font-size: 14px; color: #047857; margin: 0; font-weight: 600; }
.business-details-grid { display: flex; flex-direction: column; gap: 10px; }
.detail-row { display: flex; justify-content: space-between; padding: 8px 12px; background: white; border-radius: 8px; }
.detail-label { font-size: 13px; color: #6b7280; }
.detail-value { font-size: 14px; font-weight: 600; color: #1e293b; }

/* Not Found Card */
.not-found-card { text-align: center; padding: 40px 20px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 12px; margin-top: 20px; }
.not-found-icon { color: #f87171; margin-bottom: 16px; }
.not-found-card h4 { color: #dc2626; margin-bottom: 8px; }
.not-found-card p { color: #7f1d1d; margin-bottom: 20px; }

/* Business Summary */
.business-summary { display: flex; align-items: center; gap: 14px; padding: 16px; background: #f8fafc; border-radius: 12px; margin-bottom: 24px; }
.summary-icon { width: 48px; height: 48px; background: #dbeafe; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #0f4c81; }
.summary-icon.fire { background: #fee2e2; color: #dc2626; }
.business-summary strong { display: block; font-size: 16px; color: #1e293b; }
.business-summary p { font-size: 13px; color: #64748b; margin: 0; }

/* Safety Measures Section */
.safety-measures-section { margin-bottom: 32px; }
.section-title-bar { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 20px; padding: 16px; background: #fff7ed; border: 1px solid #fed7aa; border-radius: 12px; }
.title-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.title-icon.orange { background: #ffedd5; color: #ea580c; }
.title-icon.blue { background: #dbeafe; color: #2563eb; }
.section-title-bar h4 { font-size: 15px; font-weight: 600; color: #1e293b; margin: 0 0 4px; }
.section-title-bar p { font-size: 13px; color: #64748b; margin: 0; }

.safety-measures-grid { display: grid; grid-template-columns: 1fr; gap: 10px; }
.safety-measure-item { display: flex; align-items: center; gap: 12px; padding: 14px 16px; background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 10px; cursor: pointer; transition: all 0.2s; }
.safety-measure-item:hover { border-color: #ea580c; }
.safety-measure-item.selected { background: #fff7ed; border-color: #ea580c; }
.measure-checkbox { width: 22px; height: 22px; border: 2px solid #d1d5db; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.2s; color: transparent; }
.safety-measure-item.selected .measure-checkbox { background: #ea580c; border-color: #ea580c; color: white; }
.measure-text { font-size: 14px; color: #1e293b; }

/* Recommendations Section */
.recommendations-section { margin-bottom: 24px; }
.recommendations-list { display: flex; flex-direction: column; gap: 10px; }
.recommendation-item { display: flex; align-items: center; gap: 10px; }
.rec-number { width: 28px; height: 28px; background: #e2e8f0; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 600; color: #64748b; flex-shrink: 0; }
.recommendation-item .form-input { flex: 1; }
.remove-rec-btn { width: 36px; height: 36px; background: #fee2e2; border: none; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #dc2626; cursor: pointer; flex-shrink: 0; }
.remove-rec-btn:hover { background: #fecaca; }
.add-rec-btn { display: flex; align-items: center; gap: 8px; padding: 12px 20px; background: #f1f5f9; border: 2px dashed #cbd5e1; border-radius: 10px; color: #64748b; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
.add-rec-btn:hover { background: #e2e8f0; border-color: #94a3b8; color: #475569; }

/* Other Charges Section */
.other-charges-section { margin-top: 24px; padding: 24px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; }
.section-header-bar { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 2px solid #dc2626; }
.header-icon { width: 36px; height: 36px; background: #fee2e2; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #dc2626; }
.section-header-bar h4 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0; flex: 1; }
.charges-count { font-size: 12px; color: #64748b; background: #e2e8f0; padding: 4px 10px; border-radius: 20px; }

.add-charge-form { display: flex; flex-direction: column; gap: 16px; }
.charge-step { display: flex; flex-direction: column; gap: 8px; }
.charge-step-row { display: flex; gap: 16px; align-items: flex-end; }
.quantity-step { flex: 0 0 120px; }
.step-badge { display: inline-block; font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 20px; width: fit-content; }
.step-badge.blue { background: #dbeafe; color: #1d4ed8; }
.step-badge.green { background: #d1fae5; color: #059669; }
.step-badge.purple { background: #ede9fe; color: #7c3aed; }

.btn-add-charge { display: flex; align-items: center; gap: 8px; padding: 14px 24px; background: #059669; color: white; border: none; border-radius: 10px; font-weight: 600; cursor: pointer; transition: all 0.2s; white-space: nowrap; }
.btn-add-charge:hover:not(:disabled) { background: #047857; }
.btn-add-charge:disabled { opacity: 0.5; cursor: not-allowed; }

.loading-charges { display: flex; align-items: center; gap: 10px; padding: 16px; background: #fef3c7; border-radius: 8px; color: #92400e; font-size: 14px; }
.spinner.blue { border-color: rgba(30, 64, 175, 0.2); border-top-color: #1d4ed8; }

/* Added Charges Table */
.added-charges-table { margin-top: 20px; overflow: hidden; border-radius: 10px; border: 1px solid #e2e8f0; }
.charges-table { width: 100%; border-collapse: collapse; }
.charges-table th, .charges-table td { padding: 12px 16px; text-align: left; }
.charges-table thead { background: #dc2626; color: white; }
.charges-table thead th { font-size: 13px; font-weight: 600; }
.charges-table tbody tr { border-bottom: 1px solid #e2e8f0; }
.charges-table tbody tr:last-child { border-bottom: none; }
.charges-table tbody td { font-size: 14px; color: #1e293b; background: white; }
.charges-table tfoot { background: #f1f5f9; }
.charges-table tfoot th { font-size: 14px; font-weight: 700; color: #dc2626; }
.text-center { text-align: center; }
.text-right { text-align: right; }

.remove-charge-btn { padding: 6px 8px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; color: #dc2626; cursor: pointer; transition: all 0.2s; }
.remove-charge-btn:hover { background: #fee2e2; }

/* Fee Summary */
.fee-summary { margin-top: 24px; padding: 20px; background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%); border-radius: 12px; color: white; }
.fee-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; }
.fee-row.total { border-top: 1px solid rgba(255,255,255,0.2); margin-top: 8px; padding-top: 16px; font-size: 18px; font-weight: 700; }

/* Summary Section */
.summary-section { margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid #e2e8f0; }
.summary-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.summary-header h4 { font-size: 16px; font-weight: 600; color: #1e293b; }
.edit-btn { padding: 6px 14px; background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 13px; color: #dc2626; cursor: pointer; }
.edit-btn:hover { background: #e2e8f0; }
.summary-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.summary-item { padding: 12px; background: #f8fafc; border-radius: 8px; }
.summary-label { display: block; font-size: 11px; color: #64748b; text-transform: uppercase; margin-bottom: 4px; }
.summary-value { font-size: 14px; font-weight: 500; color: #1e293b; }

/* Safety Summary */
.safety-summary { padding: 16px; background: #f8fafc; border-radius: 10px; }
.measures-list { display: flex; flex-wrap: wrap; gap: 8px; }
.measure-tag { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; background: #d1fae5; color: #065f46; border-radius: 20px; font-size: 12px; font-weight: 500; }
.no-measures { color: #64748b; font-size: 14px; margin: 0; }

/* Invoice Preview */
.invoice-preview { margin-bottom: 24px; }
.invoice-preview h4 { font-size: 16px; font-weight: 600; color: #1e293b; margin-bottom: 12px; }
.invoice-table { width: 100%; border-collapse: collapse; }
.invoice-table th, .invoice-table td { padding: 12px 16px; text-align: left; border: 1px solid #e2e8f0; }
.invoice-table thead { background: #f8fafc; }
.invoice-table thead th { font-size: 13px; font-weight: 600; color: #475569; }
.invoice-table tbody td { font-size: 14px; color: #1e293b; }
.invoice-table tfoot { background: #dc2626; color: white; }
.invoice-table tfoot th { font-size: 15px; }

/* Payment Notice */
.payment-notice { display: flex; gap: 16px; padding: 20px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 12px; margin-bottom: 24px; }
.payment-notice.fire { background: #fef2f2; border-color: #fecaca; }
.notice-icon { color: #2563eb; flex-shrink: 0; }
.payment-notice.fire .notice-icon { color: #dc2626; }
.payment-notice strong { display: block; color: #1e40af; margin-bottom: 4px; }
.payment-notice.fire strong { color: #991b1b; }
.payment-notice p { font-size: 14px; color: #3b82f6; margin: 0; }
.payment-notice.fire p { color: #ef4444; }

/* Declaration */
.declaration-box { padding: 20px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 24px; }
.declaration-label { display: flex; align-items: flex-start; gap: 12px; cursor: pointer; }
.declaration-label input { display: none; }
.declaration-label .checkbox-mark { width: 22px; height: 22px; border: 2px solid #d1d5db; border-radius: 6px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.declaration-label input:checked + .checkbox-mark { background: #dc2626; border-color: #dc2626; color: white; }
.declaration-text { font-size: 14px; color: #475569; line-height: 1.6; }

/* Buttons */
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 10px; padding: 14px 24px; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 600; cursor: pointer; border: none; text-decoration: none; transition: all 0.2s; }
.btn-lg { padding: 16px 32px; font-size: 16px; }
.btn-primary { background: #dc2626; color: white; }
.btn-primary:hover:not(:disabled) { background: #b91c1c; }
.btn-success { background: #059669; color: white; }
.btn-success:hover:not(:disabled) { background: #047857; }
.btn-secondary { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
.btn-secondary:hover { background: #e2e8f0; }
.btn-outline { background: transparent; color: #475569; border: 1px solid #e2e8f0; }
.btn-outline:hover { background: #f8fafc; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.w-100 { width: 100%; }
.mt-3 { margin-top: 12px; }
.form-actions { display: flex; justify-content: flex-end; margin-top: 24px; padding-top: 24px; border-top: 1px solid #e2e8f0; }

/* Spinner */
.spinner { width: 20px; height: 20px; border: 2px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; animation: spin 0.8s linear infinite; }
.spinner.small { width: 16px; height: 16px; }
.spinner.white { border-color: rgba(255,255,255,0.3); border-top-color: white; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Success Card */
.success-card .form-card-body { padding: 48px; }
.success-content { text-align: center; }
.success-icon { width: 80px; height: 80px; background: #d1fae5; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #059669; margin: 0 auto 24px; }
.success-icon.fire { background: #fee2e2; color: #dc2626; }
.success-content h2 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 24px; font-weight: 700; color: #059669; margin-bottom: 8px; }
.success-message { color: #64748b; margin-bottom: 32px; }

.invoice-card { background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%); border-radius: 14px; padding: 24px; color: white; margin-bottom: 24px; }
.invoice-card.fire { background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%); }
.invoice-label { font-size: 12px; opacity: 0.8; text-transform: uppercase; }
.invoice-number { display: block; font-size: 24px; font-weight: 800; margin: 8px 0 16px; letter-spacing: 1px; }
.invoice-amount { display: flex; justify-content: space-between; padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.2); }
.invoice-amount span { opacity: 0.8; }
.invoice-amount strong { font-size: 20px; }

.notification-card { display: flex; align-items: flex-start; gap: 16px; padding: 20px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 12px; margin-bottom: 24px; text-align: left; }
.notification-icon { width: 44px; height: 44px; background: #fbbf24; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; flex-shrink: 0; }
.notification-text strong { display: block; color: #92400e; margin-bottom: 4px; }
.notification-text p { font-size: 14px; color: #a16207; margin: 0; }

.next-steps { text-align: left; margin-bottom: 32px; }
.next-steps h4 { font-size: 16px; font-weight: 600; color: #1e293b; margin-bottom: 16px; text-align: center; }
.next-steps ul { list-style: none; padding: 0; margin: 0; }
.next-steps li { display: flex; gap: 16px; padding: 16px; background: #f8fafc; border-radius: 10px; margin-bottom: 8px; }
.step-num { width: 28px; height: 28px; background: #dc2626; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; flex-shrink: 0; }
.next-steps li strong { display: block; color: #1e293b; margin-bottom: 2px; }
.next-steps li p { font-size: 13px; color: #64748b; margin: 0; }

.success-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

/* Transitions */
.slide-fade-enter-active { transition: all 0.3s ease; }
.slide-fade-leave-active { transition: all 0.2s ease; }
.slide-fade-enter-from { opacity: 0; transform: translateX(20px); }
.slide-fade-leave-to { opacity: 0; transform: translateX(-20px); }

/* Responsive */
@media (max-width: 1024px) {
  .content-grid { grid-template-columns: 1fr; }
  .info-panel { position: static; }
  .progress-stepper { flex-wrap: wrap; gap: 16px; }
  .step-connector { display: none; }
}

@media (max-width: 768px) {
  /* Form panel first on mobile — citizen came to apply for a Fire Safety
     Compliance Certificate, not read tips. Same treatment as the other
     citizen forms in this repo. */
  .content-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .info-panel { order: 2; }
  .form-panel { order: 1; }

  /* Hero + section chrome. */
  .container { padding: 0 12px; }
  .page-hero { padding: 20px 0 40px !important; }
  .page-title { font-size: 20px !important; margin-bottom: 4px; }
  .hero-subtitle,
  .breadcrumb,
  .breadcrumb-current { font-size: 12px; }
  .content-section { padding: 24px 0 32px; }

  /* Info card (below form) — compact. */
  .info-card { padding: 16px 14px; border-radius: 12px; }
  .info-card-header { gap: 10px; }
  .info-icon { width: 40px; height: 40px; }
  .info-icon svg { width: 20px; height: 20px; }
  .info-card h3 { font-size: 16px; }
  .info-card .card-subtitle,
  .info-card p,
  .info-description { font-size: 12.5px; line-height: 1.4; }
  .info-features { gap: 8px; margin-top: 12px; }
  .info-feature,
  .feature-item { padding: 10px; gap: 8px; }
  .info-feature-icon,
  .feature-icon { width: 28px; height: 28px; }
  .info-feature-icon svg,
  .feature-icon svg { width: 14px; height: 14px; }
  .info-feature strong,
  .feature-item strong { font-size: 12.5px; }
  .info-feature p,
  .info-feature .text-muted,
  .feature-item p { font-size: 11px; margin-top: 2px; }
  .tips-card { padding: 14px; border-radius: 10px; margin-top: 12px; }
  .tips-card h4,
  .tips-card .tips-title { font-size: 13px; margin-bottom: 6px; }
  .tips-card ul { padding-left: 16px; margin: 0; }
  .tips-card li { font-size: 12px; line-height: 1.5; }

  /* Quick-actions section (Print Fire License / Track Application shortcuts). */
  .quick-actions-section { padding: 14px; margin-bottom: 14px; }
  .quick-actions-section h4 { font-size: 13px; margin-bottom: 10px; }
  .quick-action-btn { padding: 10px 12px; gap: 10px; margin-bottom: 8px; }
  .quick-action-btn .action-icon { width: 36px; height: 36px; border-radius: 8px; }
  .quick-action-btn .action-content strong { font-size: 12.5px; }
  .quick-action-btn .action-content span { font-size: 11px; }

  /* Form-card body. */
  .form-card { border-radius: 12px; }
  .form-card-header { padding: 12px 14px; }
  .form-card-header .step-text { font-size: 14px; }
  .form-card-body { padding: 16px 14px; }
  .form-intro { font-size: 12.5px; margin-bottom: 12px; }
  .form-group { margin-bottom: 12px; }
  .form-label { font-size: 12.5px; margin-bottom: 4px; }
  .form-input,
  .form-select,
  .form-textarea { font-size: 14px; padding: 10px 12px; min-height: 42px; }

  .form-row,
  .summary-grid { grid-template-columns: 1fr; gap: 10px; }

  /* Method-selection tiles (search-existing / new-application). */
  .method-selection {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  .method-card,
  .method-selection > * { padding: 12px 10px !important; gap: 8px !important; }
  .method-card h4,
  .method-card .method-title { font-size: 13px; line-height: 1.2; }
  .method-card p,
  .method-card .method-desc { font-size: 11.5px; line-height: 1.35; }

  /* Charges builder row. */
  .charge-step-row { flex-direction: column; gap: 10px; }
  .quantity-step { flex: 1; }
  .btn-add-charge { width: 100%; justify-content: center; padding: 12px 16px; font-size: 13.5px; }

  /* Charges table — scroll if too wide, tighter cells. */
  .charges-table-wrapper,
  .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .charges-table { font-size: 11.5px; }
  .charges-table th,
  .charges-table td { padding: 8px 10px; white-space: nowrap; }

  .success-actions { flex-direction: column; gap: 10px; }
}

@media (max-width: 480px) {
  .container { padding: 0 10px; }
  .page-title { font-size: 18px !important; }
  .form-card-body { padding: 14px 12px; }
  .form-input, .form-select, .form-textarea { font-size: 13.5px; min-height: 40px; }
  .method-selection { grid-template-columns: 1fr; }
  .charges-table { font-size: 11px; }
  .charges-table th, .charges-table td { padding: 6px 8px; }
}

/* Quick Actions Section */
.quick-actions-section { padding: 20px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 20px; }
.quick-actions-section h4 { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #1e293b; margin-bottom: 16px; }
.quick-action-btn { display: flex; align-items: center; gap: 14px; width: 100%; padding: 14px 16px; background: white; border: 2px solid #e2e8f0; border-radius: 10px; text-decoration: none; cursor: pointer; transition: all 0.2s; margin-bottom: 10px; text-align: left; }
.quick-action-btn:last-child { margin-bottom: 0; }
.quick-action-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); border-color: #2563eb; }
.quick-action-btn .action-icon { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.quick-action-btn .action-icon.blue { background: #dbeafe; color: #2563eb; }
.quick-action-btn .action-icon.green { background: #d1fae5; color: #059669; }
.quick-action-btn .action-content { flex: 1; }
.quick-action-btn .action-content strong { display: block; font-size: 14px; color: #1e293b; margin-bottom: 2px; }
.quick-action-btn .action-content span { font-size: 12px; color: #64748b; }
.quick-action-btn .action-arrow { color: #94a3b8; transition: transform 0.2s; }
.quick-action-btn:hover .action-arrow { transform: translateX(4px); color: #2563eb; }

/* Modal Overlay */
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(15, 23, 42, 0.6); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }

/* Status Modal */
.status-modal { background: white; border-radius: 20px; width: 100%; max-width: 540px; max-height: 90vh; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 24px; border-bottom: 1px solid #e2e8f0; }
.modal-title-wrapper { display: flex; align-items: center; gap: 14px; }
.modal-icon { width: 48px; height: 48px; background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%); border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #2563eb; }
.modal-header h3 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 20px; font-weight: 700; color: #1e293b; margin: 0; }
.modal-close { width: 40px; height: 40px; border-radius: 10px; border: none; background: #f1f5f9; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.modal-close:hover { background: #fee2e2; color: #dc2626; }
.modal-body { padding: 24px; overflow-y: auto; flex: 1; }
.modal-description { color: #64748b; font-size: 14px; margin-bottom: 20px; }

/* Search Tabs */
.search-tabs { display: flex; gap: 8px; margin-bottom: 20px; }
.search-tab { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px 16px; background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 10px; font-size: 14px; font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.2s; }
.search-tab:hover { border-color: #2563eb; }
.search-tab.active { background: #eff6ff; border-color: #2563eb; color: #2563eb; }

/* Status Error */
.status-error { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px; color: #dc2626; font-size: 14px; margin-bottom: 16px; }

/* Status Result */
.status-result-header { display: flex; align-items: flex-start; gap: 16px; padding: 20px; border-radius: 12px; margin-bottom: 20px; }
.status-result-header.success { background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); }
.status-result-header.expired { background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); }
.status-result-header.pending { background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); }
.status-result-header.warning { background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); }
.status-icon-wrapper { width: 56px; height: 56px; border-radius: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.status-result-header.success .status-icon-wrapper { background: #a7f3d0; color: #059669; }
.status-result-header.expired .status-icon-wrapper { background: #fecaca; color: #dc2626; }
.status-result-header.pending .status-icon-wrapper { background: #fde68a; color: #d97706; }
.status-result-header.warning .status-icon-wrapper { background: #fde68a; color: #d97706; }
.status-info { flex: 1; }
.status-badge { display: inline-block; padding: 6px 14px; border-radius: 20px; font-size: 13px; font-weight: 700; }
.status-badge.success { background: #059669; color: white; }
.status-badge.expired { background: #dc2626; color: white; }
.status-badge.pending { background: #d97706; color: white; }
.status-badge.warning { background: #d97706; color: white; }
.status-message { font-size: 14px; color: #475569; margin: 8px 0 0; }

/* Status Details */
.status-details { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; margin-bottom: 20px; }
.detail-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e2e8f0; }
.detail-row:last-child { border-bottom: none; }
.detail-label { font-size: 13px; color: #64748b; }
.detail-value { font-size: 13px; font-weight: 600; color: #1e293b; }

/* Status Timeline */
.status-timeline { margin-bottom: 20px; }
.status-timeline h4 { font-size: 14px; font-weight: 600; color: #1e293b; margin-bottom: 16px; }
.timeline-steps { position: relative; }
.timeline-step { display: flex; align-items: flex-start; gap: 12px; padding-bottom: 16px; position: relative; }
.timeline-step:last-child { padding-bottom: 0; }
.timeline-step::before { content: ''; position: absolute; left: 13px; top: 28px; bottom: 0; width: 2px; background: #e2e8f0; }
.timeline-step:last-child::before { display: none; }
.timeline-step.completed::before { background: #10b981; }
.step-marker { width: 28px; height: 28px; border-radius: 50%; background: #e2e8f0; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: #64748b; flex-shrink: 0; z-index: 1; }
.timeline-step.completed .step-marker { background: #10b981; color: white; }
.timeline-step.current .step-marker { background: #2563eb; color: white; box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.2); }
.step-content { flex: 1; padding-top: 4px; }
.step-content strong { font-size: 13px; color: #1e293b; display: block; }
.step-date { font-size: 11px; color: #64748b; }

/* Status Actions */
.status-actions { display: flex; gap: 10px; }
.status-actions .btn { flex: 1; }

/* Button Block */
.btn-block { width: 100%; }

/* Modal Transitions */
.modal-fade-enter-active, .modal-fade-leave-active { transition: all 0.3s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-from .status-modal, .modal-fade-leave-to .status-modal { transform: scale(0.9) translateY(20px); }
</style>